/**
 * FeedbackWidget Component
 *
 * In-app feedback collection for customer insights.
 * Floating button that expands to feedback form.
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  MessageSquare,
  X,
  Send,
  ThumbsUp,
  Bug,
  Lightbulb,
  HelpCircle,
  Star,
  Check
} from 'lucide-react';
import api from '../../services/api';
import { trackEvent } from '../../services/telemetry';

// Z-Index Layer System (keep in sync with ChatAssistant)
// dropdown = 100, sidebar = 200, console = 500, modal = 1000, toast = 1100
const Z_INDEX = {
  FEEDBACK_BUTTON: 'z-[800]',   // Below console button
  FEEDBACK_PANEL: 'z-[1000]',   // Modal layer - above console
  NPS_POPUP: 'z-[900]',
};

const FEEDBACK_TYPES = [
  { id: 'bug', label: 'Bug Report', icon: Bug, color: 'red' },
  { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'amber' },
  { id: 'question', label: 'Question', icon: HelpCircle, color: 'blue' },
  { id: 'praise', label: 'Love It!', icon: ThumbsUp, color: 'green' }
];

const FeedbackWidget = ({ position = 'bottom-right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('type'); // 'type', 'form', 'rating', 'success'
  const [feedbackType, setFeedbackType] = useState(null);
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showQuickRating, setShowQuickRating] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);

  // Listen for console open/close to hide FAB when console is open
  useEffect(() => {
    const handleConsoleToggle = (e) => {
      setConsoleOpen(e.detail?.isOpen || false);
    };
    window.addEventListener('gabhan-console-toggle', handleConsoleToggle);
    return () => window.removeEventListener('gabhan-console-toggle', handleConsoleToggle);
  }, []);

  // Check if we should show NPS prompt
  useEffect(() => {
    const lastPrompt = localStorage.getItem('gabhan_last_nps_prompt');
    const now = Date.now();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;

    if (!lastPrompt || now - parseInt(lastPrompt) > thirtyDays) {
      // Show NPS after user has been active for a bit
      const timer = setTimeout(() => {
        setShowQuickRating(true);
      }, 60000); // 1 minute

      return () => clearTimeout(timer);
    }
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    setStep('type');
    trackEvent('feedback_widget_opened');
  };

  const handleClose = () => {
    setIsOpen(false);
    setStep('type');
    setFeedbackType(null);
    setMessage('');
    setRating(0);
  };

  const handleTypeSelect = (type) => {
    setFeedbackType(type);
    setStep('form');
    trackEvent('feedback_type_selected', { type: type.id });
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setSubmitting(true);
    try {
      await api.post('/feedback', {
        type: feedbackType?.id,
        message,
        rating,
        email: email || undefined,
        page: window.location.pathname,
        userAgent: navigator.userAgent
      });

      trackEvent('feedback_submitted', {
        type: feedbackType?.id,
        hasRating: rating > 0,
        messageLength: message.length
      });

      setStep('success');
    } catch (err) {
      console.error('Feedback submit error:', err);
      // Still show success for UX
      setStep('success');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickRating = async (score) => {
    localStorage.setItem('gabhan_last_nps_prompt', Date.now().toString());
    setShowQuickRating(false);

    try {
      await api.post('/feedback/nps', { score });
      trackEvent('nps_submitted', { score });
    } catch {
      // Ignore errors
    }
  };

  // FAB Position: Stacked above Command Console (which is at bottom-6)
  // Feedback FAB at bottom-24 (96px) avoids collision
  const positionClasses = {
    'bottom-right': 'bottom-24 right-6',  // Stacked above console FAB
    'bottom-left': 'bottom-24 left-6',
    'top-right': 'top-20 right-6',
    'top-left': 'top-20 left-6'
  };

  // Panel position: above the FAB button
  const panelPositionClasses = {
    'bottom-right': 'bottom-40 right-6',  // 160px from bottom
    'bottom-left': 'bottom-40 left-6',
    'top-right': 'top-36 right-6',
    'top-left': 'top-36 left-6'
  };

  return (
    <>
      {/* Quick NPS Rating Popup */}
      {showQuickRating && !isOpen && createPortal(
        <div className={`fixed ${panelPositionClasses[position]} ${Z_INDEX.NPS_POPUP} animate-slide-up`}>
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl p-4 w-72 border border-gray-200 dark:border-dark-700">
            <button
              onClick={() => setShowQuickRating(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              How likely are you to recommend Gabhan?
            </p>
            <div className="flex gap-1 justify-center mb-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                <button
                  key={score}
                  onClick={() => handleQuickRating(score)}
                  className={`w-6 h-6 text-xs rounded ${
                    score <= 6
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : score <= 8
                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {score}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>Not likely</span>
              <span>Very likely</span>
            </div>
          </div>
        </div>,
        document.getElementById('modal-root') || document.body
      )}

      {/* Expanded Form - Rendered in portal for proper stacking */}
      {isOpen && createPortal(
        <div className={`fixed ${panelPositionClasses[position]} ${Z_INDEX.FEEDBACK_PANEL}`}>
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl w-80 overflow-hidden border border-gray-200 dark:border-dark-700 animate-scale-in">
            {/* Header - Dark theme to match Command Console */}
            <div className="px-4 py-3 bg-slate-800 text-white flex items-center justify-between border-b border-slate-700">
              <span className="font-medium text-sm">Send Feedback</span>
              <button onClick={handleClose} className="text-slate-400 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {step === 'type' && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    What kind of feedback do you have?
                  </p>
                  {FEEDBACK_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => handleTypeSelect(type)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-dark-600 hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors text-left"
                      >
                        <div className={`w-8 h-8 rounded-lg bg-${type.color}-100 dark:bg-${type.color}-900/30 flex items-center justify-center`}>
                          <Icon size={16} className={`text-${type.color}-600`} />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {type.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {step === 'form' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    {feedbackType && (
                      <>
                        <feedbackType.icon size={16} />
                        <span>{feedbackType.label}</span>
                      </>
                    )}
                    <button
                      onClick={() => setStep('type')}
                      className="ml-auto text-brand-600 hover:text-brand-700 text-xs"
                    >
                      Change
                    </button>
                  </div>

                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us more..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    autoFocus
                  />

                  {/* Optional rating */}
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Rate your experience (optional)
                    </p>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          className="p-1"
                        >
                          <Star
                            size={20}
                            className={star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Email for follow-up */}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email for follow-up (optional)"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white text-sm"
                  />

                  <button
                    onClick={handleSubmit}
                    disabled={!message.trim() || submitting}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {submitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Feedback
                      </>
                    )}
                  </button>
                </div>
              )}

              {step === 'success' && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Thank you! 🎉
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Your feedback helps us improve Gabhan for everyone.
                  </p>
                  <button
                    onClick={handleClose}
                    className="mt-4 text-sm text-brand-600 hover:text-brand-700"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.getElementById('modal-root') || document.body
      )}

      {/* Floating Button - Hidden when Gabhan Console is open to avoid collision */}
      {!consoleOpen && (
        <div className={`fixed ${positionClasses[position]} ${Z_INDEX.FEEDBACK_BUTTON}`}>
          <button
            onClick={handleOpen}
            className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
              isOpen
                ? 'bg-gray-600 hover:bg-gray-700'
                : 'bg-brand-600 hover:bg-brand-700 hover:scale-110'
            }`}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <MessageSquare className="w-6 h-6 text-white" />
            )}
          </button>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
